import React, { useCallback, useMemo, useState } from 'react'
import { DataGrid } from 'devextreme-react/data-grid'
import { Column } from 'devextreme-react/data-grid'
import { Badge } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { resultsAdapter } from '../../../redux/slices/resultsSlice'
import { ResultEntity } from '../../../types/results'
import { DefaultState } from '../../../redux'
import ResultFilesModal from './ResultFilesModal/ResultFilesModal'
import { renderDateTime } from '../../../utils/dateFormatter'
import { capitalizeFirstLetter } from '../../../utils/helpers'
import {
  getResult,
  removeResult,
  updateResultAction
} from '../../../redux/slices'
import { AppDispatch } from '../../../services/lib'
import ResultsPagination from './ResultsPagination'
import { IPAResultAction } from '../../../services/endpoints'
import ResultsActionCell from './ResultsActionCell'
import { showToast } from '../../../utils/types'

import './ResultsGrid.css'

const EMPTY_RESULT: ResultEntity = {
  id: '',
  analyzer: { id: '', name: '' },
  status: '',
  job_id: null,
  script: { id: '', name: '' },
  configs: [],
  files: [],
  created_at: '',
  updated_at: '',
  recycled: null,
  retention_lock: false
}

const selectResultsState = (state: DefaultState) => state.results
const { selectAll: selectAllResults } =
  resultsAdapter.getSelectors(selectResultsState)

const STATUS_BADGE_COLORS: Record<string, string> = {
  complete: 'success',
  finished: 'success',
  success: 'success',
  running: 'primary',
  queued: 'info',
  pending: 'warning',
  processing: 'warning',
  failed: 'danger',
  error: 'danger',
  canceled: 'secondary',
  cancelled: 'secondary'
}

const configurationDisplayValue = (data: ResultEntity): string =>
  data.configs && data.configs.length ? data.configs[0].name : '—'

type ResultsGridProps = {
  canManageResults: boolean
}

const ResultsGrid: React.FC<ResultsGridProps> = ({ canManageResults }) => {
  const results = useAppSelector(selectAllResults)
  const resultsError = useAppSelector((state) => state.results.error)
  const pageSize = useAppSelector((state) => state.results.pageSize)
  const pageIndex = useAppSelector((state) => state.results.pageIndex)
  const chunkOffset = useAppSelector((state) => state.results.offset)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedResult, setSelectedResult] =
    useState<ResultEntity>(EMPTY_RESULT)
  const dispatch: AppDispatch = useDispatch()

  const visibleResults = useMemo(() => {
    if (!results.length) {
      return results
    }

    const normalizedPageSize = pageSize > 0 ? pageSize : results.length
    const startIndex = Math.max(0, pageIndex * normalizedPageSize - chunkOffset)
    const endIndex = startIndex + normalizedPageSize

    return results.slice(startIndex, endIndex)
  }, [results, pageIndex, pageSize, chunkOffset])

  const dataSource = useMemo(() => visibleResults, [visibleResults])

  const handleOpenFiles = useCallback((result: ResultEntity) => {
    setSelectedResult(result)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedResult(EMPTY_RESULT)
  }, [])

  const renderStatusCell = useCallback(({ data }: { data: ResultEntity }) => {
    if (!data.status) {
      return '—'
    }

    const normalized = data.status.toLowerCase()
    const badgeColor = STATUS_BADGE_COLORS[normalized] || 'dark'
    const displayText = capitalizeFirstLetter(normalized)

    return (
      <Badge color={badgeColor} pill>
        {displayText}
      </Badge>
    )
  }, [])

  const renderFilesCell = useCallback(
    ({ data }: { data: ResultEntity }) => {
      const fileCount = data.files.length

      if (!fileCount) {
        return '—'
      }

      return (
        <button
          type="button"
          className="result-files-link"
          onClick={() => handleOpenFiles(data)}
        >
          {`View ${fileCount} ${fileCount === 1 ? 'File' : 'Files'}`}
        </button>
      )
    },
    [handleOpenFiles]
  )

  const renderCreatedAtCell = useCallback(
    ({ data }: { data: ResultEntity }) =>
      renderDateTime({ value: data.created_at }),
    []
  )

  const renderUpdatedAtCell = useCallback(
    ({ data }: { data: ResultEntity }) =>
      renderDateTime({ value: data.updated_at }),
    []
  )

  const handleDelete = useCallback(
    async (result: ResultEntity) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete result ${result.id}?`
      )

      if (!confirmed) {
        return
      }

      await dispatch(removeResult(result.id))
    },
    [dispatch]
  )

  const handleResultAction = useCallback(
    async (result: ResultEntity, action: IPAResultAction) => {
      const response = await dispatch(
        updateResultAction({ id: result.id, action })
      )

      if (updateResultAction.rejected.match(response)) {
        const payload = response.payload as { message?: string } | undefined
        const message =
          payload?.message ||
          `Unable to ${action} result ${result.id}. Data was refreshed.`

        showToast(message, 'error', 3000)
        await dispatch(getResult(result.id))
      }
    },
    [dispatch]
  )

  return (
    <>
      <div className="resultsPage-grid">
        <div className="resultsPage-grid__container">
          <DataGrid
            elementAttr={{ class: 'resultsPage-grid__datagrid' }}
            dataSource={dataSource}
            keyExpr="id"
            columnAutoWidth={false}
            columnResizingMode="widget"
            rowAlternationEnabled={true}
            showBorders={true}
            allowColumnResizing={true}
            noDataText="Run a search to view results."
            paging={{
              enabled: false
            }}
          >
            <Column
              caption="Action"
              width={80}
              allowSorting={false}
              cellRender={({ data }: { data: ResultEntity }) => (
                <ResultsActionCell
                  data={data}
                  canManageResults={canManageResults}
                  onDelete={handleDelete}
                  onResultAction={handleResultAction}
                />
              )}
            />
            <Column dataField="job_id" caption="Job ID" width={60} />
            <Column dataField="id" caption="Result ID" />
            <Column
              dataField="analyzer.name"
              caption="Analyzer"
              minWidth={120}
            />
            <Column dataField="script.name" caption="Script" />
            <Column
              dataField="configs"
              caption="Configuration"
              allowSorting={false}
              calculateDisplayValue={configurationDisplayValue}
            />
            <Column
              dataField="status"
              caption="Status"
              allowSorting={false}
              cellRender={renderStatusCell}
              width={100}
            />
            <Column
              dataField="created_at"
              caption="Created"
              cellRender={renderCreatedAtCell}
            />
            <Column
              dataField="updated_at"
              caption="Updated"
              cellRender={renderUpdatedAtCell}
            />
            <Column
              dataField="files"
              caption="Files"
              allowSorting={false}
              cellRender={renderFilesCell}
              width={105}
            />
          </DataGrid>
          <ResultsPagination />
        </div>
      </div>

      {isModalOpen && (
        <ResultFilesModal
          selectedResult={selectedResult}
          resultsError={resultsError}
          toggleModal={handleCloseModal}
          show={isModalOpen}
        />
      )}
    </>
  )
}

export default React.memo(ResultsGrid)
